/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { create } from 'zustand'
import { apiClient, User } from '../lib/api/client'
import { STORAGE_KEYS } from '../config/api'

interface AuthState {
  // État
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  initialize: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string) => Promise<void>
  logout: () => void
  loadUser: () => Promise<void>
  verifyEmailCode: (email: string, code: string) => Promise<void>
  resendVerification: (email?: string) => Promise<void>
  loginWithGoogle: (idToken: string) => Promise<boolean>
  loginWithApple: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  clearError: () => void
  setUser: (user: User | null) => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // État initial
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  // Initialiser le store au chargement de l'app
  initialize: async () => {
    set({ isLoading: true })

    try {
      const accessToken = apiClient.getAccessToken()

      if (accessToken) {
        await get().loadUser()
      } else {
        set({ user: null, isAuthenticated: false })
      }
    } catch (error) {
      console.error("[AuthStore] ❌ Erreur lors de l'initialisation:", error)
      get().logout()
    } finally {
      set({ isLoading: false })
    }
  },

  // Connexion avec email/password
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null })

    try {
      // console.warn('[AuthStore] 🔑 Tentative de connexion...')

      // Appeler l'API de connexion
      await apiClient.login({ email, password })

      // Charger les informations de l'utilisateur
      await get().loadUser()

      set({ isLoading: false })
      // console.warn('[AuthStore] ✅ Connexion réussie')
    } catch (error) {
      console.error('[AuthStore] ❌ Erreur de connexion:', error)
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      set({
        isLoading: false,
        error: errorMessage,
        isAuthenticated: false,
        user: null,
      })
      throw error
    }
  },

  // Inscription avec email/password
  register: async (email: string, password: string, name: string) => {
    set({ isLoading: true, error: null })

    try {
      // console.warn("[AuthStore] 📝 Tentative d'inscription...")

      // Appeler l'API d'inscription
      await apiClient.register({ email, password, name })

      set({ isLoading: false })
      // console.warn('[AuthStore] ✅ Inscription réussie, email de vérification envoyé')
    } catch (error) {
      console.error("[AuthStore] ❌ Erreur d'inscription:", error)
      set({
        error: error instanceof Error ? error.message : "Erreur d'inscription",
        isLoading: false,
      })
      throw error
    }
  },

  // Déconnexion
  logout: () => {
    // console.warn('[AuthStore] 🚺 Déconnexion...')
    apiClient.logout()
    set({ user: null, isAuthenticated: false, error: null })
    // console.warn('[AuthStore] ✅ Déconnexion réussie')
  },

  // Charger les informations de l'utilisateur connecté
  loadUser: async () => {
    if (!apiClient.isAuthenticated()) {
      set({ user: null, isAuthenticated: false })
      return
    }

    try {
      const user = await apiClient.getCurrentUser()

      // Sauvegarder l'utilisateur dans le localStorage aussi
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))

      set({
        user,
        isAuthenticated: true,
        error: null,
      })
    } catch (error) {
      console.error('Failed to load user:', error)

      // Si hors ligne, utiliser les données du cache localStorage
      if (!navigator.onLine) {
        // console.warn('[AuthStore] Mode hors ligne détecté, utilisation du cache localStorage')
        const cachedUser = localStorage.getItem(STORAGE_KEYS.USER)
        if (cachedUser) {
          try {
            const user = JSON.parse(cachedUser)
            set({
              user,
              isAuthenticated: true,
              error: null,
            })
            return
          } catch (parseError) {
            console.error('[AuthStore] Erreur de parsing du cache utilisateur:', parseError)
          }
        }
      }

      // Token invalide ou expiré (et pas en mode offline), déconnecter
      get().logout()
    }
  },

  // Vérifier l'email avec le code à 6 chiffres
  verifyEmailCode: async (email: string, code: string) => {
    set({ isLoading: true, error: null })

    try {
      await apiClient.verifyEmailCode(email, code)
      await get().loadUser()
      set({ isLoading: false })
    } catch (error) {
      console.error('[AuthStore] ❌ Erreur de vérification:', error)
      set({
        error: error instanceof Error ? error.message : 'Erreur de vérification',
        isLoading: false,
      })
      throw error
    }
  },

  // Renvoyer le code de vérification par email
  resendVerification: async (email?: string) => {
    const { user } = get()
    const targetEmail = email ?? user?.email
    if (!targetEmail) {
      throw new Error('Email requis pour renvoyer le code')
    }

    set({ isLoading: true, error: null })

    try {
      await apiClient.resendVerification(targetEmail)
      set({ isLoading: false })
    } catch (error) {
      console.error('[AuthStore] ❌ Erreur lors du renvoi:', error)
      set({
        error: error instanceof Error ? error.message : 'Erreur lors du renvoi',
        isLoading: false,
      })
      throw error
    }
  },

  // Connexion avec Google OAuth
  loginWithGoogle: async (idToken: string) => {
    set({ isLoading: true, error: null })

    try {
      const response = await apiClient.loginWithGoogle(idToken)

      // Charger les informations de l'utilisateur
      await get().loadUser()

      set({ isLoading: false })

      return response.is_new_user || false
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Google login failed'
      set({
        isLoading: false,
        error: errorMessage,
        isAuthenticated: false,
        user: null,
      })
      throw error
    }
  },

  // Connexion avec Apple OAuth (stub pour Phase 1 Étape 4)
  loginWithApple: async () => {
    set({ isLoading: true, error: null })

    try {
      // console.warn('[AuthStore] 🔴 OAuth Apple non implémenté (Étape 4)')
      set({
        error: 'Authentification Apple non disponible pour le moment',
        isLoading: false,
      })
      throw new Error('OAuth Apple non implémenté')
    } catch (error) {
      console.error('[AuthStore] ❌ Erreur OAuth Apple:', error)
      set({
        error: error instanceof Error ? error.message : 'Erreur OAuth Apple',
        isLoading: false,
      })
      throw error
    }
  },

  // Demande de réinitialisation de mot de passe (envoi email)
  forgotPassword: async (email: string) => {
    set({ isLoading: true, error: null })
    try {
      await apiClient.forgotPassword(email)
      set({ isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erreur lors de la demande',
        isLoading: false,
      })
      throw error
    }
  },

  // Réinitialisation du mot de passe via code à 6 chiffres
  resetPassword: async (email: string, code: string, newPassword: string) => {
    set({ isLoading: true, error: null })
    try {
      await apiClient.resetPassword(email, code, newPassword)
      set({ isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erreur lors de la réinitialisation',
        isLoading: false,
      })
      throw error
    }
  },

  // Changement de mot de passe (utilisateur connecté)
  changePassword: async (currentPassword: string, newPassword: string) => {
    set({ isLoading: true, error: null })
    try {
      await apiClient.changePassword(currentPassword, newPassword)
      set({ isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erreur lors du changement',
        isLoading: false,
      })
      throw error
    }
  },

  // Effacer l'erreur
  clearError: () => {
    set({ error: null })
  },

  // Définir l'utilisateur (utilisé par les routes protégées)
  setUser: (user: User | null) => {
    set({ user, isAuthenticated: user !== null })
    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER)
    }
  },
}))
